import { useWorkspaceText } from '../interface-copy';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import runtimeCopy from '@/i18n/ns/workspace-knowledge-runtime';
import editorCopy from '@/i18n/ns/workspace-editors';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useCompany } from '@/company/context';
import { OverflowMenu } from '@/components/ui/menu';
import { useNarrowViewport } from '@/lib/mobile';
import { useWorkspace } from '../context';
import { WorkspaceIcon } from '../icons';
import { announceKnowledgePagesChanged } from '../knowledge-agent-events';
import { parseWorkspaceRoute } from '../navigation';
import { EmptyState, ErrorState, LoadingState } from '../primitives';
import { emptyTurnContext } from '../types';
import { errorCode, knowledgePagesApi, pagesErrorMessage } from './api';
import { PageEditor } from './editor-panel';
import { plural, prefersReducedMotion, revisionOrigin, sectionAnchor, shortDate } from './format';
import { PageHistory } from './history';
import { changePreview, toInlineChanges, type MarkInput } from './marks';
import { PageActionDialog, type PageAction } from './page-actions';
import { MarkedPage, usePagePlan } from './page-body';
import { SuggestionCard, type SuggestionDecision } from './suggestion-card';
import { SuggestionSheet } from './suggestion-sheet';
import { usePageView, type KnowledgeTreeState } from './use-wiki';
import type { PageSuggestion, PageView, WriteResult } from './types';

export type PageLayout = 'full' | 'docked';

export interface PageFocus {
  suggestion?: string;
  section?: string;
  /** A link to an exact revision (older citations). */
  revision?: string;
}

/** Where the page view goes when someone opens another page or a new-page draft. */
export interface PageNavigation {
  /** `replace` swaps the current history entry (after a delete, Back must not return). */
  openPage: (id: string, focus?: PageFocus, options?: { replace?: boolean }) => void;
  newPage?: (parentId: string) => void;
}

type Mode = 'read' | 'history' | 'edit';

const noMarks: MarkInput[] = [];

/** "Revision 6 · edited by Sara Holm · 24 Sept". */
function metaLine(
  view: PageView,
  locale: string,
  copy: Readonly<Record<keyof typeof runtimeCopy.en, string>>,
  revisionLabel: string,
) {
  const revision = view.revision;
  if (!revision) return copy.noTextYet;
  return [
    formatCopy(revisionLabel, { number: revision.number }),
    revisionOrigin(revision, copy),
    shortDate(revision.createdAt, locale),
  ]
    .filter(Boolean)
    .join(' · ');
}

function Breadcrumb({
  view,
  tree,
  onOpen,
}: {
  view: PageView;
  tree: KnowledgeTreeState | null;
  onOpen: (id: string) => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ancestors = tree?.index?.ancestors(view.page.id) ?? [];
  if (view.page.isRoot || !ancestors.length) return null;
  return (
    <nav className="ws-wiki-breadcrumb" aria-label={uiText('Where this page is')}>
      <ol>
        {ancestors.map((page) => (
          <li key={page.id}>
            <button type="button" className="ws-text-button" onClick={() => onOpen(page.id)}>
              {page.title}
            </button>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function PageLinks({
  title,
  pages,
  onOpen,
}: {
  title: string;
  pages: { id: string; title: string }[];
  onOpen: (id: string) => void;
}) {
  if (!pages.length) return null;
  return (
    <section className="ws-wiki-links" aria-label={title}>
      <h2>{title}</h2>
      <div>
        {pages.map((page) => (
          <button
            key={page.id}
            type="button"
            className="ws-wiki-chip"
            onClick={() => onOpen(page.id)}
          >
            {page.title}
          </button>
        ))}
      </div>
    </section>
  );
}

function EmptyPage({
  view,
  canPublish,
  companyName,
  onEdit,
}: {
  view: PageView;
  canPublish: boolean;
  companyName: string;
  onEdit: () => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const root = view.page.isRoot;
  return (
    <section
      className="ws-wiki-empty"
      aria-label={root ? uiText('Describe your company') : uiText('Empty page')}
    >
      <h2>
        {root
          ? uiText('Describe {value1}', { value1: companyName })
          : uiText('This page has no text yet.')}
      </h2>
      <p>
        {root
          ? uiText(
              'Poneva reads this page before every answer. Start with who you are, what you sell and how you work; details go on the pages under it.',
            )
          : canPublish
            ? uiText('Write it, or ask Poneva in chat to draft it for you.')
            : uiText(
                'Suggest a first version, and a publisher decides whether it becomes the page.',
              )}
      </p>
      <button type="button" className="ws-button primary" onClick={onEdit}>
        <WorkspaceIcon name="edit" size={14} />
        {canPublish ? uiText('Start writing') : uiText('Suggest a first version')}
      </button>
    </section>
  );
}

/** Scroll a card or a mark into view and move focus to it, gently. */
function reveal(element: Element | null | undefined, focus = true) {
  if (!element) return;
  (element as HTMLElement).scrollIntoView?.({
    block: 'nearest',
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
  });
  if (focus) (element as HTMLElement).focus?.({ preventScroll: true });
}

/**
 * One knowledge page, read or edited: its place in the tree, the approved text
 * with open suggestions marked inline, the suggestion cards (in the margin at
 * full width, under the changed passage when docked beside a chat, one at a
 * time in a bottom sheet on a phone), history, children and backlinks.
 */
export function KnowledgePageView({
  pageId,
  layout,
  focus,
  tree,
  navigation,
}: {
  pageId: string;
  layout: PageLayout;
  focus?: PageFocus;
  tree: KnowledgeTreeState | null;
  navigation: PageNavigation;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const locale = useChatInterfaceLocale();
  const runtime = useNamespaceCopy(runtimeCopy);
  const editor = useNamespaceCopy(editorCopy);
  const ws = useWorkspace();
  const company = useCompany();
  const phone = useNarrowViewport();
  const { view, error, missing, retry } = usePageView(pageId);
  const [mode, setMode] = useState<Mode>('read');
  // The text an edit starts from, fixed when editing begins: a reread of the
  // page meanwhile must neither reset the editor nor move its base revision.
  const [editBase, setEditBase] = useState<{
    revisionId: string | null;
    markdown: string;
    title: string;
  } | null>(null);
  const [show, setShow] = useState(true);
  const [focused, setFocused] = useState<string | null>(focus?.suggestion ?? null);
  const [busy, setBusy] = useState<string | null>(null);
  const deciding = useRef(false);
  const [decisionError, setDecisionError] = useState('');
  const [action, setAction] = useState<PageAction | null>(null);
  const [historyStart, setHistoryStart] = useState<string | undefined>(undefined);
  const root = useRef<HTMLDivElement>(null);
  const cardHeaders = useRef(new Map<string, HTMLButtonElement>());
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (focus?.suggestion) {
      setFocused(focus.suggestion);
      setShow(true);
      setMode((current) => (current === 'edit' ? current : 'read'));
    }
  }, [focus?.suggestion]);
  const canPublish = view?.canPublish ?? false;
  const canSuggest = company.data?.membership?.permissions?.privateWork !== false;
  const suggestions = view?.suggestions ?? [];
  const markdown = view?.revision?.markdown ?? '';
  const numbered = useMemo(
    () => new Map(suggestions.map((suggestion, index) => [suggestion.id, index])),
    [suggestions],
  );
  // Each open suggestion's changes against the current text; null when its
  // ranges do not describe this text (it is then shown on its card only).
  const resolved = useMemo(
    () =>
      suggestions.map((suggestion) =>
        suggestion.state === 'open' && suggestion.changes.length
          ? toInlineChanges(markdown, suggestion.changes)
          : null,
      ),
    [markdown, suggestions],
  );
  const marks = useMemo(() => {
    if (!show || !view) return noMarks;
    return resolved.flatMap((changes, index) => (changes ? [{ index, changes }] : []));
  }, [show, view, resolved]);
  const plan = usePagePlan(markdown, view?.revision?.sections ?? [], marks);
  const unmarked = (suggestion: PageSuggestion, index: number) => {
    if (suggestion.state !== 'open' || !suggestion.changes.length) return null;
    if (!resolved[index]) return 'mismatch' as const;
    return plan.unplaced.has(index) ? ('overlap' as const) : null;
  };
  const hasMarkedText = plan.chunks.some((chunk) => chunk.kind !== 'plain');
  const focusedIndex = focused !== null ? (numbered.get(focused) ?? null) : null;

  // A section link scrolls to its section once the text is on screen.
  useEffect(() => {
    if (!view?.revision || !focus?.section || mode !== 'read') return;
    const anchor = sectionAnchor(focus.section);
    reveal([...(root.current?.querySelectorAll('[id]') ?? [])].find((node) => node.id === anchor));
  }, [focus?.section, view?.revision?.id, mode]);
  // A suggestion in focus from a link or a chat: its card comes into view.
  const focusedLoaded = Boolean(
    focus?.suggestion && view?.suggestions.some((item) => item.id === focus.suggestion),
  );
  useEffect(() => {
    if (!focus?.suggestion || !view) return;
    reveal(cardHeaders.current.get(focus.suggestion), false);
  }, [focus?.suggestion, focusedLoaded]);

  const titleOf = (id: string | null) =>
    (id ? tree?.index?.byId.get(id)?.title : tree?.index?.root.title) ??
    (id ? editor.pageFallbackAnother : editor.pageFallbackOverview);
  // The host guards unsaved edits: Knowledge and the canvas both ask through the
  // workspace's one leave dialog, so this does not ask a second time.
  const openPage = (id: string) => navigation.openPage(id);
  const beginEdit = () => {
    if (!view) return;
    setEditBase({
      revisionId: view.revision?.id ?? null,
      markdown: view.revision?.markdown ?? '',
      title: view.page.title,
    });
    setMode('edit');
  };

  function focusFromMarker(index: number) {
    const suggestion = suggestions[index];
    if (!suggestion) return;
    setFocused(suggestion.id);
    reveal(cardHeaders.current.get(suggestion.id));
  }
  function focusFromCard(suggestion: PageSuggestion) {
    setFocused(suggestion.id);
    const index = numbered.get(suggestion.id);
    if (index === undefined) return;
    reveal(
      root.current?.querySelector(
        `[data-suggestion-mark="${index}"], [data-suggestion-marks~="${index}"]`,
      ),
      false,
    );
  }

  async function decide(suggestion: PageSuggestion, decision: SuggestionDecision) {
    // A ref, not state: a quick second press must not send a second decision.
    if (!view || deciding.current) return;
    deciding.current = true;
    setBusy(suggestion.id);
    setDecisionError('');
    try {
      if (decision === 'accept') {
        const accepted = await knowledgePagesApi.accept(
          suggestion.id,
          view.revision?.id ?? null,
          Boolean(view.page.archivedAt) && suggestion.kind !== 'archive_page',
        );
        ws.notify(
          accepted.archived
            ? uiText('Accepted. The page is archived.')
            : accepted.revisionNumber
              ? uiText('Accepted. The page is now revision {value1}.', {
                  value1: accepted.revisionNumber,
                })
              : uiText('Accepted.'),
          null,
          'success',
        );
      } else if (decision === 'reject') {
        await knowledgePagesApi.reject(suggestion.id);
        ws.notify(uiText('Suggestion rejected. The page stays as it is.'), null, 'success');
      } else {
        await knowledgePagesApi.withdraw(suggestion.id);
        ws.notify(uiText('Your suggestion is withdrawn.'), null, 'success');
      }
      if (focused === suggestion.id) setFocused(null);
      announceKnowledgePagesChanged();
    } catch (failure) {
      if (!mounted.current) return;
      const code = errorCode(failure);
      // The page or the suggestion moved on: show what is true now.
      if (
        code === 'revision_conflict' ||
        code === 'suggestion_outdated' ||
        code === 'proposal_not_pending' ||
        code === 'page_archived'
      )
        announceKnowledgePagesChanged();
      setDecisionError(
        code === 'revision_conflict'
          ? editor.revisionConflict
          : pagesErrorMessage(
              failure,
              uiText('We could not save your decision. Please try again.'),
              locale,
            ),
      );
    } finally {
      deciding.current = false;
      if (mounted.current) setBusy(null);
    }
  }

  // A publisher brings an archived page back where it was.
  async function restoreArchived() {
    if (!view || busy !== null) return;
    setBusy('restore');
    try {
      await knowledgePagesApi.unarchive(view.page.id);
      ws.notify(uiText('Restored. The page informs answers again.'), null, 'success');
      announceKnowledgePagesChanged();
    } catch (failure) {
      ws.notify(
        pagesErrorMessage(
          failure,
          uiText('We could not restore this page. Please try again.'),
          locale,
        ),
      );
    } finally {
      if (mounted.current) setBusy(null);
    }
  }

  function ask() {
    if (!view) return;
    const link = `/#/workspace/knowledge?${new URLSearchParams({ canvas: 'page', id: view.page.id })}`;
    ws.send(
      `Question about the knowledge page “${view.page.title}” (${link}): `,
      emptyTurnContext(),
      false,
    );
  }

  function afterWrite(result: WriteResult) {
    setMode('read');
    if (result.outcome === 'suggested' && result.suggestionId) {
      setShow(true);
      setFocused(result.suggestionId);
      ws.notify(
        uiText('Your suggestion is sent. A publisher decides whether it becomes the page.'),
        null,
        'success',
      );
    } else if (result.outcome === 'saved')
      ws.notify(
        uiText('Saved. The page is now revision {value1}.', {
          value1: result.revisionNumber ?? 'new',
        }),
        null,
        'success',
      );
    else if (result.outcome === 'unchanged') ws.notify(uiText('Nothing changed.'), null, 'success');
  }

  // Clicking a link to another page opens it here instead of leaving the view.
  function followPageLinks(event: React.MouseEvent) {
    // While editing, a click on a link places the cursor; it never navigates.
    if (mode === 'edit') {
      if ((event.target as Element).closest?.('[contenteditable="true"] a[href]'))
        event.preventDefault();
      return;
    }
    const anchor = (event.target as Element).closest?.('a[href]');
    if (!anchor || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey)
      return;
    const href = anchor.getAttribute('href') ?? '';
    const hash = href.slice(href.indexOf('#'));
    if (!hash.startsWith('#/workspace/knowledge')) return;
    const target = parseWorkspaceRoute(hash).canvas;
    if (target?.kind !== 'page' || !target.id) return;
    event.preventDefault();
    navigation.openPage(target.id, { section: target.section });
  }

  if (missing && !view)
    return (
      <div className="ws-wiki-page ws-canvas-body">
        <EmptyState
          icon="knowledge"
          title={uiText('This page is not available.')}
          description={uiText(
            'It may have been deleted, archived or moved out of reach, or you may no longer be in this company.',
          )}
        />
      </div>
    );
  if (error && !view)
    return (
      <div className="ws-wiki-page ws-canvas-body">
        <ErrorState message={error} onRetry={retry} />
      </div>
    );
  if (!view) return <LoadingState label={uiText('Loading page…')} />;

  const { page, revision } = view;
  const canEdit = !page.archivedAt && (canPublish || canSuggest);
  const layoutName = phone ? 'phone' : layout;
  const cardFor = (suggestion: PageSuggestion, footer?: ReactNode) => {
    const index = numbered.get(suggestion.id) ?? 0;
    const why = unmarked(suggestion, index);
    const changes = resolved[index];
    return (
      <SuggestionCard
        key={suggestion.id}
        ref={(node) => {
          if (node) cardHeaders.current.set(suggestion.id, node);
          else cardHeaders.current.delete(suggestion.id);
        }}
        suggestion={suggestion}
        number={index + 1}
        canPublish={canPublish}
        focused={focused === suggestion.id}
        busy={busy !== null}
        archived={Boolean(page.archivedAt)}
        unmarked={why}
        preview={why === 'overlap' && changes ? changePreview(markdown, changes) : null}
        titleOf={titleOf}
        onFocus={() => focusFromCard(suggestion)}
        onDecide={(decision) => void decide(suggestion, decision)}
        footer={footer}
      />
    );
  };
  const showCards = show && suggestions.length > 0 && mode === 'read';
  // Docked: cards of marked suggestions go under their passage, the rest above the text.
  const inlineIndexes = new Set(
    plan.chunks.flatMap((chunk) => (chunk.kind === 'plain' ? [] : chunk.indexes)),
  );
  const loose = suggestions.filter((_, index) => !inlineIndexes.has(index));
  const moreItems = [
    ...(!page.isRoot && !page.archivedAt && (canPublish || canSuggest)
      ? [
          {
            key: 'move',
            label: editor.pageMove,
            onSelect: () => setAction('move'),
          },
          {
            key: 'archive',
            label: editor.pageArchive,
            tone: 'danger' as const,
            separated: true,
            disabled: view.children.length > 0,
            reason: view.children.length > 0 ? editor.pageMoveArchiveReason : undefined,
            onSelect: () => setAction('archive'),
          },
        ]
      : []),
    ...(page.archivedAt && canPublish
      ? [
          {
            key: 'restore',
            label: editor.pageRestore,
            onSelect: () => void restoreArchived(),
          },
          {
            key: 'delete',
            label: editor.pageDeletePermanently,
            tone: 'danger' as const,
            separated: true,
            onSelect: () => setAction('delete'),
          },
        ]
      : []),
    ...(revision
      ? [
          {
            key: 'export',
            label: editor.pageExportMarkdown,
            icon: <WorkspaceIcon name="download" size={14} />,
            onSelect: () =>
              void knowledgePagesApi
                .download(page.id)
                .catch((failure) =>
                  ws.notify(
                    pagesErrorMessage(failure, uiText('We could not export this page.'), locale),
                  ),
                ),
          },
        ]
      : []),
  ];

  const toolbar = (
    <div className="ws-wiki-toolbar" role="toolbar" aria-label={editor.pageActions}>
      {suggestions.length > 0 && mode === 'read' && (
        <button
          type="button"
          className="ws-wiki-toggle"
          aria-pressed={show}
          onClick={() => {
            setShow((value) => !value);
            setFocused(null);
          }}
        >
          <WorkspaceIcon name="eye" size={14} />
          {plural(suggestions.length, runtime.suggestion, undefined, locale)} ·{' '}
          {show ? uiText('shown') : uiText('hidden')}
        </button>
      )}
      {revision && (
        <button
          type="button"
          className="ws-button ws-button-quiet"
          aria-pressed={mode === 'history'}
          onClick={() => setMode((current) => (current === 'history' ? 'read' : 'history'))}
        >
          {uiRich('{value1} History', { value1: <WorkspaceIcon name="history" size={14} /> })}
        </button>
      )}
      <button type="button" className="ws-button" onClick={ask}>
        {uiRich('{value1} Ask in chat', { value1: <WorkspaceIcon name="chat" size={14} /> })}
      </button>
      {canEdit && (
        <button type="button" className="ws-button primary" onClick={beginEdit}>
          <WorkspaceIcon name="edit" size={14} />
          {canPublish ? uiText('Edit') : uiText('Suggest edits')}
        </button>
      )}
      <OverflowMenu
        label={uiText('More actions for {value1}', { value1: page.title })}
        items={moreItems}
        variant="secondary"
      />
    </div>
  );

  const body =
    mode === 'edit' && editBase ? (
      <PageEditor
        target={{
          kind: 'page',
          pageId: page.id,
          baseRevisionId: editBase.revisionId,
          markdown: editBase.markdown,
          title: editBase.title,
          openSuggestions: suggestions.length,
        }}
        index={tree?.index ?? null}
        canPublish={canPublish}
        onDone={afterWrite}
        onCancel={() => setMode('read')}
      />
    ) : mode === 'history' ? (
      <PageHistory
        view={view}
        canPublish={canPublish}
        initialRevision={historyStart}
        onRestored={() => setMode('read')}
      />
    ) : !revision && !hasMarkedText ? (
      canEdit ? (
        <EmptyPage
          view={view}
          canPublish={canPublish}
          companyName={ws.companyName}
          onEdit={beginEdit}
        />
      ) : (
        <p className="ws-muted">{uiText('This page has no text yet.')}</p>
      )
    ) : (
      <MarkedPage
        plan={plan}
        label={page.title}
        focused={focusedIndex}
        onMarker={focusFromMarker}
        focusSection={focus?.section}
        after={
          layoutName === 'docked' && showCards
            ? (indexes) => (
                <div className="ws-suggestion-inline">
                  {indexes.map((index) => suggestions[index] && cardFor(suggestions[index]))}
                </div>
              )
            : undefined
        }
      />
    );

  return (
    <div
      className="ws-wiki-page"
      data-layout={layoutName}
      data-mode={mode}
      ref={root}
      onClick={followPageLinks}
    >
      <header className="ws-wiki-page-head">
        <Breadcrumb view={view} tree={tree} onOpen={openPage} />
        {page.isRoot && (
          <p className="ws-wiki-root-badge">
            {uiRich('{value1} Poneva reads this page in every chat', {
              value1: <WorkspaceIcon name="home" size={12} />,
            })}
          </p>
        )}
        {mode !== 'edit' && <h1>{page.title}</h1>}
        {mode !== 'edit' && (
          <p className="ws-wiki-meta">{metaLine(view, locale, runtime, editor.revisionLabel)}</p>
        )}
        {page.archivedAt && (
          <div className="ws-page-banner" role="note">
            <p>
              {uiRich(
                'This page is archived. It no longer informs answers; its history is kept. {value1}',
                { value1: !canPublish && ' A publisher can restore it.' },
              )}
            </p>
            {canPublish && (
              <div className="ws-page-banner-actions">
                <button
                  type="button"
                  className="ws-button"
                  disabled={busy !== null}
                  onClick={() => void restoreArchived()}
                >
                  {uiText('Restore page')}
                </button>
                <button
                  type="button"
                  className="ws-button danger"
                  disabled={busy !== null}
                  onClick={() => setAction('delete')}
                >
                  {uiText('Delete permanently')}
                </button>
              </div>
            )}
          </div>
        )}
        {focus?.revision && revision && focus.revision !== revision.id && mode === 'read' && (
          <div className="ws-page-banner" role="note">
            <p>
              {uiText('This link points to an earlier revision. You are reading the current text.')}
            </p>
            <button
              type="button"
              className="ws-button"
              onClick={() => {
                setHistoryStart(focus.revision);
                setMode('history');
              }}
            >
              {uiText('Show it in History')}
            </button>
          </div>
        )}
        {/* While editing, the editor's own bar holds the actions. */}
        {mode !== 'edit' && toolbar}
      </header>
      {decisionError && (
        <p className="ws-error" role="alert">
          {decisionError}
        </p>
      )}
      <div
        className="ws-wiki-page-grid"
        data-cards={showCards && layoutName === 'full' ? 'margin' : undefined}
      >
        <div className="ws-wiki-page-main">
          {showCards && layoutName === 'docked' && loose.length > 0 && (
            <div className="ws-suggestion-inline ws-suggestion-loose">
              {loose.map((suggestion) => cardFor(suggestion))}
            </div>
          )}
          {body}
          {mode === 'read' && (
            <>
              <PageLinks
                title={
                  page.isRoot
                    ? uiText('Pages')
                    : uiText('Pages under {value1}', { value1: page.title })
                }
                pages={view.children}
                onOpen={openPage}
              />
              <PageLinks title={uiText('Linked from')} pages={view.backlinks} onOpen={openPage} />
            </>
          )}
        </div>
        {layoutName === 'full' && mode === 'read' && suggestions.length > 0 && (
          <aside className="ws-suggestion-margin" aria-label={uiText('Suggestions')}>
            {show ? (
              suggestions.map((suggestion) => cardFor(suggestion))
            ) : (
              <p className="ws-suggestion-hidden-note">
                {uiText(
                  'Suggestions are hidden. You are reading the page as everyone else sees it.',
                )}
              </p>
            )}
          </aside>
        )}
      </div>
      {layoutName === 'phone' && showCards && (
        <SuggestionSheet
          suggestions={suggestions}
          focused={focused}
          onFocus={(id) => setFocused(id)}
          renderCard={(suggestion, pager) => cardFor(suggestion, pager)}
        />
      )}
      {!show && suggestions.length > 0 && layoutName !== 'full' && mode === 'read' && (
        <p className="ws-suggestion-hidden-note">
          {uiText('Suggestions are hidden. You are reading the page as everyone else sees it.')}
        </p>
      )}
      {action && tree?.index && (
        <PageActionDialog
          action={action}
          view={view}
          index={tree.index}
          canPublish={canPublish}
          onClose={() => setAction(null)}
          onDone={(result) => {
            setAction(null);
            // A deleted page is gone: show the page it was under.
            if (result.outcome === 'deleted')
              navigation.openPage(page.parentId ?? tree.index!.root.id, undefined, {
                replace: true,
              });
            if (result.outcome === 'suggested' && result.suggestionId) {
              setShow(true);
              setFocused(result.suggestionId);
            }
          }}
        />
      )}
    </div>
  );
}
